import React, { useEffect, useState, createContext, useContext } from 'react';
export interface CurrencyRate {
  symbol: string;
  code: string;
  rate: number;
}
interface CurrencyContextType {
  currency: CurrencyRate;
  countryCode: string;
  loading: boolean;
  formatPrice: (usdPrice: number) => string;
  currencyCopy: string;
}
// Exact fallback rates from user specification
const RATE_MAP: Record<string, CurrencyRate> = {
  US: {
    symbol: '$',
    code: 'USD',
    rate: 1
  },
  MX: {
    symbol: '$',
    code: 'MXN',
    rate: 17
  },
  CO: {
    symbol: '$',
    code: 'COP',
    rate: 4000
  },
  PE: {
    symbol: 'S/',
    code: 'PEN',
    rate: 3.7
  },
  CL: {
    symbol: '$',
    code: 'CLP',
    rate: 900
  },
  ES: {
    symbol: '€',
    code: 'EUR',
    rate: 0.92
  },
  AR: {
    symbol: '$',
    code: 'ARS',
    rate: 1180
  },
  EC: {
    symbol: '$',
    code: 'USD',
    rate: 1
  },
  UY: {
    symbol: '$',
    code: 'UYU',
    rate: 42
  },
  BR: {
    symbol: 'R$',
    code: 'BRL',
    rate: 5.7
  },
  DE: {
    symbol: '€',
    code: 'EUR',
    rate: 0.92
  },
  FR: {
    symbol: '€',
    code: 'EUR',
    rate: 0.92
  },
  IT: {
    symbol: '€',
    code: 'EUR',
    rate: 0.92
  },
  GB: {
    symbol: '£',
    code: 'GBP',
    rate: 0.79
  },
  CR: {
    symbol: '₡',
    code: 'CRC',
    rate: 510
  },
  DO: {
    symbol: 'RD$',
    code: 'DOP',
    rate: 60
  },
  GT: {
    symbol: 'Q',
    code: 'GTQ',
    rate: 7.75
  },
  HN: {
    symbol: 'L',
    code: 'HNL',
    rate: 25.4
  },
  PA: {
    symbol: '$',
    code: 'USD',
    rate: 1
  },
  PY: {
    symbol: '₲',
    code: 'PYG',
    rate: 7700
  },
  BO: {
    symbol: 'Bs',
    code: 'BOB',
    rate: 6.91
  },
  VE: {
    symbol: 'Bs.',
    code: 'VES',
    rate: 36.6
  },
  NI: {
    symbol: 'C$',
    code: 'NIO',
    rate: 36.8
  },
  SV: {
    symbol: '$',
    code: 'USD',
    rate: 1
  }
};
const CURRENCY_COPY_MAP: Record<string, string> = {
  MX: 'Paga en pesos mexicanos 🇲🇽',
  CO: 'Paga en pesos colombianos 🇨🇴',
  PE: 'Paga en soles peruanos 🇵🇪',
  CL: 'Paga en pesos chilenos 🇨🇱',
  ES: 'Pago en euros 🇪🇸',
  DE: 'Pago en euros 🇪🇺',
  FR: 'Pago en euros 🇪🇺',
  IT: 'Pago en euros 🇪🇺',
  AR: 'Paga en pesos argentinos 🇦🇷',
  BR: 'Paga en reales brasileños 🇧🇷',
  UY: 'Paga en pesos uruguayos 🇺🇾',
  VE: 'Paga en bolívares 🇻🇪',
  BO: 'Paga en bolivianos 🇧🇴',
  CR: 'Paga en colones 🇨🇷',
  DO: 'Paga en pesos dominicanos 🇩🇴',
  GT: 'Paga en quetzales 🇬🇹',
  HN: 'Paga en lempiras 🇭🇳',
  NI: 'Paga en córdobas 🇳🇮',
  PY: 'Paga en guaraníes 🇵🇾',
  GB: 'Pay in British pounds 🇬🇧',
  US: 'Pago seguro en USD 🇺🇸'
};
const DEFAULT_CURRENCY: CurrencyRate = {
  symbol: '$',
  code: 'USD',
  rate: 1
};
const CACHE_KEY = 'detected_currency';
const CACHE_KEY_COUNTRY = 'detected_country';
const CACHE_TTL = 60 * 60 * 1000; // 1 hour
const CurrencyContext = createContext<CurrencyContextType | undefined>(undefined);
export function CurrencyProvider({
  children
}: {
  children: ReactNode;
}) {
  const [currency, setCurrency] = useState<CurrencyRate>(DEFAULT_CURRENCY);
  const [countryCode, setCountryCode] = useState('US');
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    const detectCurrency = async () => {
      try {
        // Check cache first
        const cached = localStorage.getItem(CACHE_KEY);
        const cachedCountry = localStorage.getItem(CACHE_KEY_COUNTRY);
        if (cached && cachedCountry) {
          const {
            data,
            timestamp
          } = JSON.parse(cached);
          if (Date.now() - timestamp < CACHE_TTL) {
            setCurrency(data);
            setCountryCode(cachedCountry);
            setLoading(false);
            return;
          }
        }
        // Fetch if no valid cache
        const res = await fetch('https://ipapi.co/json/', {
          signal: AbortSignal.timeout(4000)
        });
        const data = await res.json();
        const detected = data?.country_code || 'US';
        const detectedRate = RATE_MAP[detected] || DEFAULT_CURRENCY;
        setCurrency(detectedRate);
        setCountryCode(detected);
        // Save to cache
        localStorage.setItem(CACHE_KEY, JSON.stringify({
          data: detectedRate,
          timestamp: Date.now()
        }));
        localStorage.setItem(CACHE_KEY_COUNTRY, detected);
      } catch {
        setCurrency(DEFAULT_CURRENCY);
        setCountryCode('US');
      } finally {
        setLoading(false);
      }
    };
    detectCurrency();
  }, []);
  const formatPrice = (usdPrice: number) => {
    if (loading) return '...';
    const converted = usdPrice * currency.rate;
    // USD — no suffix needed
    if (currency.code === 'USD') {
      return `$${converted.toFixed(2)}`;
    }
    // Large rates (COP, CLP, ARS, PYG, CRC) — no decimals, with currency code
    if (currency.rate > 100) {
      return `${currency.symbol}${Math.round(converted).toLocaleString('es-ES')} ${currency.code}`;
    }
    // Medium/small rates — 2 decimals, with currency code for ambiguous symbols
    const formatted = `${currency.symbol}${converted.toFixed(2)}`;
    if (currency.symbol === '$' || currency.symbol === 'Bs' || currency.symbol === 'Bs.') {
      return `${formatted} ${currency.code}`;
    }
    return formatted;
  };
  const currencyCopy = CURRENCY_COPY_MAP[countryCode] || CURRENCY_COPY_MAP['US'];
  return <CurrencyContext.Provider value={{
    currency,
    countryCode,
    loading,
    formatPrice,
    currencyCopy
  }} data-id="element-114">
      {children}
    </CurrencyContext.Provider>;
}
export function useCurrency() {
  const context = useContext(CurrencyContext);
  if (context === undefined) {
    throw new Error('useCurrency must be used within a CurrencyProvider');
  }
  return context;
}